import { API_ENDPOINTS, buildApiUrl } from "../config/api";

export type UserListItem = {
  id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  role?: string;
  created_at?: string;
};

export type RoleListItem = {
  name?: string;
  role?: string;
  title?: string;
};

export type RoleTableItem = {
  id: string;
  name: string;
  created_at: string | undefined;
};

export type RoleDetails = {
  id: string;
  name: string;
  created_at?: string;
  permissions: string[];
};

export type PermissionListItem = {
  id: string;
  name: string;
  description?: string;
  active: boolean;
};

type ApiResponse<TData> = {
  status: number;
  message: string;
  data?: TData;
};

type UserListResponseData =
  | UserListItem[]
  | {
      data?: UserListItem[];
      users?: UserListItem[];
      items?: UserListItem[];
      total?: number;
      total_items?: number;
      page?: number;
      limit?: number;
    };

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem("access_token");
  if (!token) {
    throw new Error("Token de acesso não encontrado.");
  }
  return { Authorization: `Bearer ${token}` };
}

function resolveErrorMessage(json: ApiResponse<unknown> | null, fallback: string): string {
  const fromData =
    json?.data && typeof json.data === "object" && "message" in json.data
      ? String((json.data as { message?: string }).message ?? "")
      : "";
  return fromData || json?.message || fallback;
}

export function getFullName(user: Pick<UserListItem, "first_name" | "last_name">): string {
  return `${user.first_name || ""} ${user.last_name || ""}`.trim();
}

export async function fetchUsersList(params: {
  page: number;
  limit: number;
  name?: string;
  role?: string;
}) {
  const query = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
  });
  const name = params.name?.trim();
  if (name) {
    query.set("name", name);
  }
  const role = params.role?.trim();
  if (role) {
    query.set("role", role);
  }

  const response = await fetch(buildApiUrl(`${API_ENDPOINTS.userList}?${query.toString()}`), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  const json = (await response.json().catch(() => null)) as ApiResponse<UserListResponseData> | null;
  if (!response.ok) {
    throw new Error(resolveErrorMessage(json, "Não foi possível listar usuários."));
  }

  const rawData = json?.data;
  const users = Array.isArray(rawData)
    ? rawData
    : rawData?.data || rawData?.users || rawData?.items || [];

  const total =
    !Array.isArray(rawData) && typeof rawData?.total === "number"
      ? rawData.total
      : !Array.isArray(rawData) && typeof rawData?.total_items === "number"
        ? rawData.total_items
        : null;

  return { users, total };
}

function normalizeRoleName(item: unknown): string | null {
  if (typeof item === "string") return item.trim() || null;
  if (!item || typeof item !== "object") return null;
  const candidate =
    ("name" in item ? (item as { name?: unknown }).name : undefined) ??
    ("role" in item ? (item as { role?: unknown }).role : undefined) ??
    ("title" in item ? (item as { title?: unknown }).title : undefined);
  if (typeof candidate !== "string") return null;
  return candidate.trim() || null;
}

export async function fetchRolesList() {
  const response = await fetch(buildApiUrl(API_ENDPOINTS.roleList), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  const json = (await response.json().catch(() => null)) as
    | ApiResponse<RoleListItem[] | string[] | { roles?: RoleListItem[] | string[] }>
    | null;

  if (!response.ok) {
    throw new Error(resolveErrorMessage(json, "Não foi possível listar perfis."));
  }

  const raw = json?.data;
  const items = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" && "roles" in raw
      ? (raw as { roles?: unknown }).roles
      : [];

  const list = Array.isArray(items) ? items : [];
  const names = list
    .map((item) => normalizeRoleName(item))
    .filter((value): value is string => Boolean(value));

  const unique = Array.from(new Set(names));
  unique.sort((a, b) => a.localeCompare(b, "pt-BR", { sensitivity: "base" }));
  return unique;
}

export async function fetchRolesTableList() {
  const response = await fetch(buildApiUrl(API_ENDPOINTS.roleList), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  const json = (await response.json().catch(() => null)) as
    | ApiResponse<
        | RoleTableItem[]
        | {
            data?: RoleTableItem[];
            roles?: RoleTableItem[];
            items?: RoleTableItem[];
          }
      >
    | null;

  if (!response.ok) {
    throw new Error(resolveErrorMessage(json, "Não foi possível listar perfis."));
  }

  const raw = json?.data;
  const list = Array.isArray(raw)
    ? raw
    : raw?.data || raw?.roles || raw?.items || [];

  const mapped = (Array.isArray(list) ? list : [])
    .map((item) => {
      const id = typeof (item as { id?: unknown })?.id === "string" ? String((item as { id: string }).id) : "";
      const name =
        typeof (item as { name?: unknown })?.name === "string" ? String((item as { name: string }).name) : "";
      const created_at =
        typeof (item as { created_at?: unknown })?.created_at === "string"
          ? String((item as { created_at: string }).created_at)
          : undefined;
      if (!id || !name) return null;
      const row: RoleTableItem = { id, name, created_at };
      return row;
    })
    .filter((value): value is RoleTableItem => value !== null);

  mapped.sort((a, b) => a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }));
  return mapped;
}

export async function fetchPermissionsList() {
  const response = await fetch(buildApiUrl(API_ENDPOINTS.permissionsList), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  const json = (await response.json().catch(() => null)) as ApiResponse<PermissionListItem[]> | null;
  if (!response.ok) {
    throw new Error(resolveErrorMessage(json, "Não foi possível listar permissões."));
  }

  const data = json?.data;
  const list: PermissionListItem[] = Array.isArray(data) ? data : [];
  return list.filter((item) => Boolean(item?.active));
}

export async function fetchRoleDetails(params: { id_role: string }) {
  const query = new URLSearchParams({ id_role: params.id_role });
  const response = await fetch(buildApiUrl(`${API_ENDPOINTS.roleGet}?${query.toString()}`), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  const json = (await response.json().catch(() => null)) as ApiResponse<RoleDetails> | null;
  if (!response.ok) {
    throw new Error(resolveErrorMessage(json, "Não foi possível carregar perfil."));
  }

  if (!json?.data) {
    throw new Error("Resposta inválida da API.");
  }

  return {
    id: json.data.id,
    name: json.data.name,
    created_at: json.data.created_at,
    permissions: Array.isArray(json.data.permissions) ? json.data.permissions : [],
  };
}

export async function updateRolePermissions(params: { id_role: string; permissions: string[] }) {
  const query = new URLSearchParams({ id_role: params.id_role });
  const payload = { permissions: params.permissions };

  const attempt = async (endpoint: string, method: "PUT" | "POST") => {
    const response = await fetch(buildApiUrl(`${endpoint}?${query.toString()}`), {
      method,
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const rawText = await response.text().catch(() => "");
    const json = (() => {
      if (!rawText) return null;
      try {
        return JSON.parse(rawText) as ApiResponse<{ message?: string }>;
      } catch {
        return null;
      }
    })();

    return { response, rawText, json };
  };

  const endpointsToTry = [
    { endpoint: API_ENDPOINTS.rolePermissionsUpdate, method: "PUT" as const },
    { endpoint: `${API_ENDPOINTS.rolePermissionsUpdate}/update`, method: "PUT" as const },
    { endpoint: API_ENDPOINTS.rolePermissionsUpdate, method: "POST" as const },
  ];

  for (let idx = 0; idx < endpointsToTry.length; idx += 1) {
    const candidate = endpointsToTry[idx];
    const { response, rawText, json } = await attempt(candidate.endpoint, candidate.method);

    if (response.ok) {
      if (json) return json;
      return { status: response.status, message: "Success" };
    }

    const isNotFound = response.status === 404;
    if (isNotFound && idx < endpointsToTry.length - 1) {
      continue;
    }

    const message = resolveErrorMessage(
      json,
      rawText ? `${rawText}` : `Não foi possível salvar permissões. (HTTP ${response.status})`
    );
    throw new Error(message);
  }

  throw new Error("Não foi possível salvar permissões.");
}

export async function createUser(payload: {
  first_name: string;
  last_name: string;
  email: string;
  role_name: string;
}) {
  const response = await fetch(buildApiUrl(API_ENDPOINTS.userCreate), {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const json = (await response.json().catch(() => null)) as ApiResponse<{ message?: string }> | null;
  if (!response.ok) {
    throw new Error(resolveErrorMessage(json, "Não foi possível criar usuário."));
  }

  return json;
}

export async function createRole(payload: { name: string }) {
  const response = await fetch(buildApiUrl(API_ENDPOINTS.roleCreate), {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const json = (await response.json().catch(() => null)) as ApiResponse<{ message?: string }> | null;
  if (!response.ok) {
    throw new Error(resolveErrorMessage(json, "Não foi possível criar perfil."));
  }

  return json;
}
